import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import { Ban, Check, Clock, Infinity } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';

import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function VisitsTable({ preselectedFutureVisits }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const userState = useUserState();
  const sessionDispatch = useUserDispatch();
  const [statusFilter, setStatusFilter] = useState('history');
  const {
    get: getVisits,
    response: getVisitsResponse,
    abort,
  } = useFetch('/UserAccount/Visits');
  const [dateRange, setDateRange] = useState(userState.dateFilter);
  const history = useHistory();

  useEffect(() => {
    let isMounted = true; // optional guard

    const fetchVisits = async () => {
      setIsLoading(true);
      const query = getQueryString(
        dateRange,
        '',
        statusFilter,
        undefined,
        userState.espbiEsi,
      );

      const response = await getVisits(query);

      if (!isMounted) return; // prevent state update after unmount/abort

      if (getVisitsResponse.ok && Array.isArray(response)) {
        const mapped = response.map((v) => ({
          id: v.id,
          serviceTitle: v.visitTitle,
          location: `${v.department}${
            v.departmentAddress ? ', ' + v.departmentAddress : ''
          }`,
          type: v.workplaceType,
          date: v.date,
          status: v.status,
          statusDisplay: v.statusDisplay,
          full: v,
        }));
        setData(mapped);
      } else {
        setData([]);
      }
      setIsLoading(false);
    };

    fetchVisits();

    // 👇 cancel pending request if filter changes or component unmounts
    return () => {
      isMounted = false;
      abort();
    };
  }, [statusFilter, userState?.espbiEsi, dateRange]);

  return (
    <DataTable
      title="Mano vizitai"
      loading={isLoading}
      columns={columns(history)}
      data={data}
      filterableColumns={['serviceTitle', 'status', 'date']}
      facetedFilters={[
        {
          columnId: 'status',
          title: 'Statusas',
          options: [
            { value: 'history', label: 'Visi', icon: Infinity },
            { value: 'registrations', label: 'Būsimi', icon: Clock },
            // { value: 'other', label: 'Kiti', icon: Ellipsis },
            { value: 'done', label: 'Įvykę', icon: Check },
            { value: 'failed', label: 'Neįvykę', icon: Ban },
          ],
        },
      ]}
      onStatusFilterChange={(status) => setStatusFilter(status)}
      preselectedFutureVisits={preselectedFutureVisits}
      onRowClick={(row) =>
        history.push('/userAccount/visits/details', { visit: row.full })
      }
    />
  );
}
const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const columns = (history) => [
  {
    accessorKey: 'serviceTitle',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PASLAUGA/GYDYTOJAS" />
    ),
    cell: ({ row }) => (
      <div className="font-medium leading-tight ">
        <div> {capitalizeFirstLetter(row.original.serviceTitle)}</div>
        <div className="text-muted-foreground text-sm ">
          {row.original.location}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="TIPAS" />
    ),
    cell: ({ row }) => row.original.type || '-',
  },
  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const rawDate = row.original.date;
      return (
        <span className="text-muted-foreground">
          {rawDate ? format(new Date(rawDate), 'yyyy-MM-dd') : '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: 'status', // used for filtering
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="STATUSAS" />
    ),
    cell: ({ row }) => {
      const display = row.original.statusDisplay;
      let variant = 'neutral';
      if (display === 'Įvykusi') variant = 'success';
      else if (display === 'Neįvykęs') variant = 'fail';
      return <Badge variant={variant}>{display}</Badge>;
    },
    enableSorting: true,
    enableFiltering: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.statusDisplay || '';
      const b = rowB.original.statusDisplay || '';
      return a.localeCompare(b, 'lt'); // or 'en' depending on your need
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <InfoButton
        onClick={() =>
          history.push('/userAccount/visits/details', {
            visit: row.original.full,
          })
        }
      >
        Vizito informacija
      </InfoButton>
    ),
  },
];
